/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { useState } from "react";

const commonTiles = [
  { label: "12\" x 12\" (1 sq ft)", widthIn: 12, heightIn: 12 },
  { label: "12\" x 24\" (2 sq ft)", widthIn: 12, heightIn: 24 },
  { label: "6\" x 6\" (0.25 sq ft)", widthIn: 6, heightIn: 6 },
  { label: "18\" x 18\" (2.25 sq ft)", widthIn: 18, heightIn: 18 },
  { label: "24\" x 24\" (4 sq ft)", widthIn: 24, heightIn: 24 },
  { label: "3\" x 6\" subway (0.125 sq ft)", widthIn: 3, heightIn: 6 },
  { label: "4\" x 12\" subway (0.33 sq ft)", widthIn: 4, heightIn: 12 },
  { label: "Custom", widthIn: 0, heightIn: 0 },
];

const layoutWaste: Record<string, number> = {
  "Straight / Grid": 0.10,
  "Brick / Offset": 0.12,
  "Diagonal / 45°": 0.15,
  "Herringbone": 0.18,
};

export function TileCalculatorClient() {
  const [areaLength, setAreaLength] = useState("");
  const [areaWidth, setAreaWidth] = useState("");
  const [tileIdx, setTileIdx] = useState(0);
  const [customTileW, setCustomTileW] = useState("");
  const [customTileH, setCustomTileH] = useState("");
  const [layout, setLayout] = useState("Straight / Grid");
  const [groutWidth, setGroutWidth] = useState("0.125");
  const [pricePerTile, setPricePerTile] = useState("");
  const [pricePerSqFt, setPricePerSqFt] = useState("");

  const areaL = parseFloat(areaLength) || 0;
  const areaW = parseFloat(areaWidth) || 0;
  const totalSqFt = areaL * areaW;

  const selected = commonTiles[tileIdx];
  const tileWIn = selected?.label === "Custom" ? (parseFloat(customTileW) || 0) : (selected?.widthIn ?? 0);
  const tileHIn = selected?.label === "Custom" ? (parseFloat(customTileH) || 0) : (selected?.heightIn ?? 0);
  const grout = parseFloat(groutWidth) || 0;

  // Tile area including grout
  const tileWSqFt = (tileWIn + grout) / 12;
  const tileHSqFt = (tileHIn + grout) / 12;
  const tileSqFtEach = (tileWIn / 12) * (tileHIn / 12);
  const tileWithGroutSqFt = tileWSqFt * tileHSqFt;

  const wasteRate = layoutWaste[layout] || 0.10;
  const grossArea = totalSqFt * (1 + wasteRate);
  const tilesNeeded = tileWithGroutSqFt > 0 ? Math.ceil(grossArea / tileWithGroutSqFt) : 0;

  // Thin-set mortar: ~50 sq ft per 50lb bag (1/4" notch trowel)
  const thinsetBags = Math.ceil(grossArea / 50);
  // Grout: ~25 sq ft per 25lb bag for 12x12 tile w/ 1/8" grout
  const groutBags = Math.ceil(grossArea / 25);

  const ptIn = pricePerTile;
  const psfIn = pricePerSqFt;
  const ppt = parseFloat(ptIn) || 0;
  const ppsf = parseFloat(psfIn) || 0;
  const tileCost = ppt > 0 ? tilesNeeded * ppt : ppsf > 0 ? grossArea * ppsf : 0;

  const hasResult = totalSqFt > 0 && tileWIn > 0 && tileHIn > 0;

  const fmt = (n: number) => n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const fmtDec = (n: number) => n.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  const inputClass = "mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary";

  return (
    <div className="mt-8 rounded-lg card-premium p-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <label className="block text-sm font-medium text-foreground">Area Length (ft)</label>
          <input type="number" value={areaLength} onChange={(e) => setAreaLength(e.target.value)} placeholder="12" className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground">Area Width (ft)</label>
          <input type="number" value={areaWidth} onChange={(e) => setAreaWidth(e.target.value)} placeholder="10" className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground">Tile Size</label>
          <select value={tileIdx} onChange={(e) => setTileIdx(Number(e.target.value))} className={inputClass}>
            {commonTiles.map((t, i) => (
              <option key={t.label} value={i}>{t.label}</option>
            ))}
          </select>
        </div>
        {selected?.label === "Custom" && (
          <>
            <div>
              <label className="block text-sm font-medium text-foreground">Tile Width (inches)</label>
              <input type="number" value={customTileW} onChange={(e) => setCustomTileW(e.target.value)} placeholder="12" className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground">Tile Height (inches)</label>
              <input type="number" value={customTileH} onChange={(e) => setCustomTileH(e.target.value)} placeholder="12" className={inputClass} />
            </div>
          </>
        )}
        <div>
          <label className="block text-sm font-medium text-foreground">Layout Pattern</label>
          <select value={layout} onChange={(e) => setLayout(e.target.value)} className={inputClass}>
            {Object.entries(layoutWaste).map(([name, waste]) => (
              <option key={name} value={name}>{name} ({(waste * 100).toFixed(0)}% waste)</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground">Grout Line Width (inches)</label>
          <input type="number" step="0.0625" value={groutWidth} onChange={(e) => setGroutWidth(e.target.value)} className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground">Price per Tile (optional)</label>
          <input type="number" value={pricePerTile} onChange={(e) => { setPricePerTile(e.target.value); if (e.target.value) setPricePerSqFt(""); }} placeholder="3.50" className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground">or Price per Sq Ft</label>
          <input type="number" value={pricePerSqFt} onChange={(e) => { setPricePerSqFt(e.target.value); if (e.target.value) setPricePerTile(""); }} placeholder="8.00" className={inputClass} />
        </div>
      </div>

      {hasResult && (
        <>
          <div className="mt-6 grid gap-4 rounded-lg bg-muted/50 p-4 sm:grid-cols-4">
            <div>
              <div className="text-sm text-muted-foreground">Floor Area</div>
              <div className="text-2xl font-bold text-foreground">{fmt(Math.round(totalSqFt))} sq ft</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Tiles Needed</div>
              <div className="text-2xl font-bold text-secondary">{fmt(tilesNeeded)}</div>
              <div className="text-xs text-muted-foreground">incl. {(wasteRate * 100).toFixed(0)}% waste</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Coverage per Tile</div>
              <div className="text-2xl font-bold text-foreground">{fmtDec(tileSqFtEach)} sq ft</div>
            </div>
            {tileCost > 0 && (
              <div>
                <div className="text-sm text-muted-foreground">Tile Cost</div>
                <div className="text-2xl font-bold text-foreground">${fmt(Math.round(tileCost))}</div>
              </div>
            )}
          </div>

          <div className="mt-4 rounded-lg border border-border p-4">
            <h3 className="mb-3 text-sm font-semibold text-foreground">Full Materials List</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Tiles</span>
                <span className="font-medium text-foreground">{fmt(tilesNeeded)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Thin-Set Mortar (50lb bags)</span>
                <span className="font-medium text-foreground">{thinsetBags}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Grout (25lb bags)</span>
                <span className="font-medium text-foreground">{groutBags}</span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
